import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';

const StaticPagesAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [staticPages, setStaticPages] = useState([]);
  const [editingStaticPage, setEditingStaticPage] = useState(null);
  const [newStaticPage, setNewStaticPage] = useState({
    page_type: '',
    title: '',
    content: '',
    is_active: true
  });

  const authHeaders = {
    'Authorization': `Bearer ${adminToken}`,
    'Content-Type': 'application/json'
  };

  // Load static pages on mount
  useEffect(() => {
    loadStaticPages();
  }, []);

  // ===============================
  // STATIC PAGES MANAGEMENT FUNCTIONS
  // ===============================

  const loadStaticPages = async () => {
    try {
      const response = await fetch(`${API}/admin/static-pages`, {
        headers: authHeaders
      });

      if (response.ok) {
        const data = await response.json();
        setStaticPages(data);
      }
    } catch (error) {
      console.error('Error loading static pages:', error);
    }
  };

  const handleCreateStaticPage = async () => {
    if (!newStaticPage.title.trim() || !newStaticPage.content.trim() || !newStaticPage.page_type.trim()) {
      showNotification('Please fill in page type, title and content', 'error');
      return;
    }

    try {
      const response = await fetch(`${API}/admin/static-pages`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(newStaticPage)
      });

      if (response.ok) {
        showNotification('Static page created successfully', 'success');
        setNewStaticPage({ page_type: '', title: '', content: '', is_active: true });
        loadStaticPages();
      } else {
        showNotification('Failed to create static page', 'error');
      }
    } catch (error) {
      console.error('Error creating static page:', error);
      showNotification('Failed to create static page', 'error');
    }
  };

  const handleUpdateStaticPage = async (pageId, updates) => {
    try {
      const formData = new FormData();
      Object.keys(updates).forEach(key => {
        if (updates[key] !== undefined && updates[key] !== null) {
          formData.append(key, updates[key]);
        }
      });

      const response = await fetch(`${API}/admin/static-pages/${pageId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });

      if (response.ok) {
        showNotification('Static page updated successfully', 'success');
        setEditingStaticPage(null);
        loadStaticPages();
      } else {
        showNotification('Failed to update static page', 'error');
      }
    } catch (error) {
      console.error('Error updating static page:', error);
      showNotification('Failed to update static page', 'error');
    }
  };

  const handleDeleteStaticPage = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) {
      return;
    }

    try {
      const response = await fetch(`${API}/admin/static-pages/${pageId}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        showNotification('Static page deleted successfully', 'success');
        loadStaticPages();
      } else {
        showNotification('Failed to delete static page', 'error');
      }
    } catch (error) {
      console.error('Error deleting static page:', error);
      showNotification('Failed to delete static page', 'error');
    }
  };

  const handleToggleStaticPageActive = async (pageId, currentStatus) => {
    await handleUpdateStaticPage(pageId, { is_active: !currentStatus });
  };

  return (
    <div className="space-y-6">
      {/* Add New Static Page */}
      {(() => {
        const pageTypeOptions = [
          { value: 'terms', label: 'Terms of Use' },
          { value: 'privacy', label: 'Privacy Policy' },
          { value: 'about', label: 'About' },
          { value: 'help', label: 'Help & Support' },
        ];
        const existingTypes = staticPages.map(p => p.page_type);
        const availableTypes = pageTypeOptions.filter(opt => !existingTypes.includes(opt.value));

        return (
          <div className={`border rounded-lg p-6 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
            <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              ➕ Add New Static Page
            </h3>
            {availableTypes.length === 0 ? (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                All page types already exist. Edit them below instead.
              </p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Page Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={newStaticPage.page_type}
                      onChange={(e) => {
                        const selected = pageTypeOptions.find(opt => opt.value === e.target.value);
                        setNewStaticPage({
                          ...newStaticPage,
                          page_type: e.target.value,
                          title: newStaticPage.title || (selected ? selected.label : '')
                        });
                      }}
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select page type...</option>
                      {availableTypes.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newStaticPage.title}
                      onChange={(e) => setNewStaticPage({...newStaticPage, title: e.target.value})}
                      placeholder="e.g., Terms of Use"
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                      }`}
                    />
                  </div>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Content (HTML supported) <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={newStaticPage.content}
                    onChange={(e) => setNewStaticPage({...newStaticPage, content: e.target.value})}
                    placeholder="Enter HTML content here..."
                    rows="8"
                    className={`w-full px-3 py-2 rounded-lg border font-mono text-sm ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                    }`}
                  />
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newStaticPage.is_active}
                      onChange={(e) => setNewStaticPage({...newStaticPage, is_active: e.target.checked})}
                      className="rounded"
                    />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Active
                    </span>
                  </label>
                </div>
                <button
                  onClick={handleCreateStaticPage}
                  disabled={!newStaticPage.page_type}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    !newStaticPage.page_type
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Create Static Page
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* Static Pages List */}
      <div className="space-y-3">
        <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          📃 All Static Pages ({staticPages.length})
        </h3>

        {staticPages.length === 0 ? (
          <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <p>No static pages yet. Create your first page above.</p>
          </div>
        ) : (
          staticPages.map((page) => (
            <div key={page.id} className={`border rounded-lg p-4 ${
              isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-white'
            } ${!page.is_active ? 'opacity-60' : ''}`}>
              {editingStaticPage === page.id ? (
                // Edit Mode - WYSIWYG Editor
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      defaultValue={page.page_type}
                      id={`page-type-${page.id}`}
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                      }`}
                    >
                      <option value="terms">Terms of Use</option>
                      <option value="privacy">Privacy Policy</option>
                      <option value="about">About</option>
                      <option value="help">Help & Support</option>
                    </select>
                    <input
                      type="text"
                      defaultValue={page.title}
                      id={`page-title-${page.id}`}
                      placeholder="Title"
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                      }`}
                    />
                  </div>

                  {/* WYSIWYG Formatting Toolbar */}
                  <div className={`flex flex-wrap gap-1 p-2 rounded-t-lg border border-b-0 ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-100 border-gray-300'}`}>
                    <button
                      type="button"
                      onClick={() => document.execCommand('bold', false, null)}
                      className={`px-3 py-1.5 rounded text-sm font-bold ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                      title="Bold (Ctrl+B)"
                    >B</button>
                    <button
                      type="button"
                      onClick={() => document.execCommand('italic', false, null)}
                      className={`px-3 py-1.5 rounded text-sm italic ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                      title="Italic (Ctrl+I)"
                    >I</button>
                    <button
                      type="button"
                      onClick={() => document.execCommand('underline', false, null)}
                      className={`px-3 py-1.5 rounded text-sm underline ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                      title="Underline (Ctrl+U)"
                    >U</button>
                    <span className={`mx-1 self-center ${isDarkMode ? 'text-gray-400' : 'text-gray-300'}`}>|</span>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          document.execCommand('formatBlock', false, e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className={`px-2 py-1.5 rounded text-sm ${isDarkMode ? 'bg-gray-500 text-white' : 'bg-white border'}`}
                      title="Heading Size"
                    >
                      <option value="">Heading</option>
                      <option value="h1">H1 - Large</option>
                      <option value="h2">H2 - Medium</option>
                      <option value="h3">H3 - Small</option>
                      <option value="p">Normal</option>
                    </select>
                    <span className={`mx-1 self-center ${isDarkMode ? 'text-gray-400' : 'text-gray-300'}`}>|</span>
                    <button
                      type="button"
                      onClick={() => document.execCommand('insertUnorderedList', false, null)}
                      className={`px-3 py-1.5 rounded text-sm ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                      title="Bullet List"
                    >• List</button>
                    <button
                      type="button"
                      onClick={() => document.execCommand('insertOrderedList', false, null)}
                      className={`px-3 py-1.5 rounded text-sm ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                      title="Numbered List"
                    >1. List</button>
                    <span className={`mx-1 self-center ${isDarkMode ? 'text-gray-400' : 'text-gray-300'}`}>|</span>
                    <div className="relative">
                      <input
                        type="color"
                        id={`text-color-${page.id}`}
                        onMouseDown={() => {
                          const sel = window.getSelection();
                          if (sel.rangeCount > 0) window._savedEditorRange = sel.getRangeAt(0).cloneRange();
                        }}
                        onChange={(e) => {
                          if (window._savedEditorRange) {
                            const sel = window.getSelection();
                            sel.removeAllRanges();
                            sel.addRange(window._savedEditorRange);
                          }
                          document.execCommand('foreColor', false, e.target.value);
                        }}
                        className="w-8 h-8 rounded cursor-pointer border-0"
                        title="Text Color"
                      />
                    </div>
                    <div className="relative">
                      <input
                        type="color"
                        id={`bg-color-${page.id}`}
                        defaultValue="#ffff00"
                        onMouseDown={() => {
                          const sel = window.getSelection();
                          if (sel.rangeCount > 0) window._savedEditorRange = sel.getRangeAt(0).cloneRange();
                        }}
                        onChange={(e) => {
                          if (window._savedEditorRange) {
                            const sel = window.getSelection();
                            sel.removeAllRanges();
                            sel.addRange(window._savedEditorRange);
                          }
                          document.execCommand('hiliteColor', false, e.target.value);
                        }}
                        className="w-8 h-8 rounded cursor-pointer border-0"
                        title="Highlight Color"
                      />
                    </div>
                    <span className={`mx-1 self-center ${isDarkMode ? 'text-gray-400' : 'text-gray-300'}`}>|</span>
                    <button
                      type="button"
                      onClick={() => document.execCommand('justifyLeft', false, null)}
                      className={`px-2 py-1.5 rounded text-sm ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                      title="Align Left"
                    >⬅️</button>
                    <button
                      type="button"
                      onClick={() => document.execCommand('justifyCenter', false, null)}
                      className={`px-2 py-1.5 rounded text-sm ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                      title="Align Center"
                    >↔️</button>
                    <button
                      type="button"
                      onClick={() => document.execCommand('justifyRight', false, null)}
                      className={`px-2 py-1.5 rounded text-sm ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                      title="Align Right"
                    >➡️</button>
                    <span className={`mx-1 self-center ${isDarkMode ? 'text-gray-400' : 'text-gray-300'}`}>|</span>
                    <button
                      type="button"
                      onClick={() => {
                        const url = prompt('Enter link URL:');
                        if (url) document.execCommand('createLink', false, url);
                      }}
                      className={`px-2 py-1.5 rounded text-sm ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                      title="Insert Link"
                    >🔗</button>
                    <button
                      type="button"
                      onClick={() => document.execCommand('removeFormat', false, null)}
                      className={`px-2 py-1.5 rounded text-sm ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                      title="Clear Formatting"
                    >🧹</button>
                  </div>

                  {/* WYSIWYG Content Editor */}
                  <div
                    contentEditable={true}
                    id={`page-content-${page.id}`}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.content) }}
                    className={`w-full min-h-[400px] max-h-[600px] overflow-y-auto px-4 py-3 rounded-b-lg border border-t-0 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode ? 'bg-gray-800 border-gray-500 text-gray-200' : 'bg-white border-gray-300 text-gray-800'
                    }`}
                    style={{ lineHeight: '1.6' }}
                  />

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => {
                        const pageType = document.getElementById(`page-type-${page.id}`).value;
                        const title = document.getElementById(`page-title-${page.id}`).value;
                        const content = document.getElementById(`page-content-${page.id}`).innerHTML;
                        handleUpdateStaticPage(page.id, { page_type: pageType, title, content });
                        setEditingStaticPage(null);
                      }}
                      className="text-sm bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 font-medium"
                    >
                      💾 Save Changes
                    </button>
                    <button
                      onClick={() => setEditingStaticPage(null)}
                      className={`text-sm px-6 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium mr-2 ${
                        page.page_type === 'terms' ? 'bg-blue-100 text-blue-800' :
                        page.page_type === 'privacy' ? 'bg-purple-100 text-purple-800' :
                        page.page_type === 'help' ? 'bg-orange-100 text-orange-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {page.page_type === 'terms' ? '📜 Terms' :
                         page.page_type === 'privacy' ? '🔒 Privacy' :
                         page.page_type === 'help' ? '❓ Help' :
                         '📖 About'}
                      </span>
                      {!page.is_active && (
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                        }`}>
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingStaticPage(page.id)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleToggleStaticPageActive(page.id, page.is_active)}
                        className={page.is_active ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'}
                      >
                        {page.is_active ? '👁️ Hide' : '👁️ Show'}
                      </button>
                      <button
                        onClick={() => handleDeleteStaticPage(page.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                  <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {page.title}
                  </h4>
                  <div
                    className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} line-clamp-3`}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.content.substring(0, 200) + (page.content.length > 200 ? '...' : '')) }}
                  />
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default StaticPagesAdmin;
